import * as React from "react"
import { Check, ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"

interface SelectProps {
  value?: string
  onValueChange?: (value: string) => void
  children: React.ReactNode
  disabled?: boolean
  placeholder?: string
}

interface SelectItemProps {
  value: string
  children: React.ReactNode
  disabled?: boolean
}

interface SelectContentProps {
  children: React.ReactNode
  className?: string
  position?: "popper" | "item"
}

interface SelectTriggerProps {
  children: React.ReactNode
  className?: string
  disabled?: boolean
  placeholder?: string
}

const SelectContext = React.createContext<{
  value?: string
  onValueChange?: (value: string) => void
  isOpen: boolean
  setIsOpen: (open: boolean) => void
  triggerRef: React.RefObject<HTMLButtonElement>
}>({
  isOpen: false,
  setIsOpen: () => {},
  triggerRef: React.createRef()
})

const Select = React.forwardRef<HTMLDivElement, SelectProps>(
  ({ value, onValueChange, children }, ref) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const rootRef   = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener("click", handleClickOutside);

      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }, []);

    // This exposes rootRef.current to the parent ref
    React.useImperativeHandle(ref, () => rootRef.current!);

    return (
      <SelectContext.Provider value={{ value, onValueChange, isOpen, setIsOpen, triggerRef }}>
        <div ref={rootRef} className="relative">
          {children}
        </div>
      </SelectContext.Provider>
    );
  }
);
Select.displayName = "Select"

const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ className, children, disabled }, ref) => {
    const { isOpen, setIsOpen, triggerRef } = React.useContext(SelectContext);
    const localRef = React.useRef<HTMLButtonElement>(null);

    React.useImperativeHandle(ref, () => localRef.current!);
    React.useImperativeHandle(triggerRef, () => localRef.current!);

    return (
      <button
        ref={localRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border px-3 py-2 text-sm shadow-sm",
          className
        )}
      >
        {/* Muestra directamente lo que venga de SelectValue */}
        {children}
        <ChevronDown
          className={cn(
            "h-4 w-4 opacity-50 transition-transform",
            isOpen && "rotate-180"
          )}
        />
      </button>
    );
  }
);

SelectTrigger.displayName = "SelectTrigger"

const SelectContent = React.forwardRef<HTMLDivElement, SelectContentProps>(
  ({ className, children, position = "popper" }, ref) => {
    const { isOpen, triggerRef } = React.useContext(SelectContext)

    if (!isOpen) return null

    return (
      <div
        ref={ref}
        className={cn(
          "absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95",
          position === "popper" && "top-full mt-1",
          className
        )}
        style={{
          width: triggerRef.current?.offsetWidth || 'auto'
        }}
      >
        <div className="p-1 max-h-[300px] overflow-y-auto">
          {children}
        </div>
      </div>
    )
  }
)
SelectContent.displayName = "SelectContent"

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps & { className?: string }>(
  ({ className, children, value, disabled }, ref) => {
    const { value: selectedValue, onValueChange, setIsOpen } = React.useContext(SelectContext)

    const handleClick = () => {
      console.log("clicked");
      if (!disabled && onValueChange) {
        console.log("value selected", value);
        onValueChange(value)
        setIsOpen(false)
      }
    }

    return (
      <div
        ref={ref}
        onClick={handleClick}
        className={cn(
          "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
          selectedValue === value && "bg-accent text-accent-foreground",
          disabled && "pointer-events-none opacity-50",
          className
        )}
      >
        <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
          {selectedValue === value && <Check className="h-4 w-4" />}
        </span>
        {children}
      </div>
    )
  }
)
SelectItem.displayName = "SelectItem"

const SelectValue = React.forwardRef<HTMLSpanElement, { placeholder?: string; children?: React.ReactNode }>(
  ({ placeholder, children }, ref) => {
    return (
      <span ref={ref} className="line-clamp-1">
        {children || placeholder}
      </span>
    )
  }
)
SelectValue.displayName = "SelectValue"

const SelectGroup = React.forwardRef<HTMLDivElement, { children: React.ReactNode; className?: string }>(
  ({ className, children }, ref) => {
    return (
      <div ref={ref} className={cn("p-1", className)}>
        {children}
      </div>
    )
  }
)
SelectGroup.displayName = "SelectGroup"

const SelectLabel = React.forwardRef<HTMLDivElement, { children: React.ReactNode; className?: string }>(
  ({ className, children }, ref) => {
    return (
      <div ref={ref} className={cn("px-2 py-1.5 text-sm font-semibold", className)}>
        {children}
      </div>
    )
  }
)
SelectLabel.displayName = "SelectLabel"

const SelectSeparator = React.forwardRef<HTMLDivElement, { className?: string }>(
  ({ className }, ref) => {
    return (
      <div ref={ref} className={cn("-mx-1 my-1 h-px bg-muted", className)} />
    )
  }
)
SelectSeparator.displayName = "SelectSeparator"

const SelectScrollUpButton = React.forwardRef<HTMLDivElement, { className?: string }>(
  ({ className }, ref) => {
    return (
      <div ref={ref} className={cn("flex cursor-default items-center justify-center py-1", className)}>
        <ChevronUp className="h-4 w-4" />
      </div>
    )
  }
)
SelectScrollUpButton.displayName = "SelectScrollUpButton"

const SelectScrollDownButton = React.forwardRef<HTMLDivElement, { className?: string }>(
  ({ className }, ref) => {
    return (
      <div ref={ref} className={cn("flex cursor-default items-center justify-center py-1", className)}>
        <ChevronDown className="h-4 w-4" />
      </div>
    )
  }
)
SelectScrollDownButton.displayName = "SelectScrollDownButton"

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
}
